/**
 * Modern template - two-column layout with sidebar
 * @param {Object} resumeData - Resume data object
 * @returns {string} HTML formatted resume
 */
function escapeHtml(str) {
  if (!str || typeof str !== "string") return "";
  return str
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;")
    .replace(/'/g, "&#039;");
}

export const modernTemplateHtml = (resumeData) => {
  if (!resumeData) return "";

  const {
    personalInformation,
    education,
    workExperience,
    additionalQualifications,
    targetJobInformation,
    skills,
    references,
  } = resumeData;

  // Start with a container that has a two-column layout and template identifier
  let html = `
<div data-template="modern" class="resume-template" style="display: flex; flex-direction: row; width: 100%;">
  <!-- Left sidebar (30% width) -->
  <div data-layout="sidebar" style="width: 30%; padding: 20px; background-color: #f0f0f0;">
`;

  // Personal Information in sidebar
  if (personalInformation) {
    // Name at the top of sidebar
    html += `<h1 data-section="personal-info" data-element="name" style="color: #2c3e50; margin-bottom: 20px; text-align: center;">${
      personalInformation.firstName || ""
    } ${personalInformation.lastName || ""}</h1>\n\n`;

    // Contact information
    html += '<div data-section="contact-info" style="margin-bottom: 30px;">\n';
    if (personalInformation.email) {
      html += `<p data-element="email" style="margin: 5px 0;"><img src="https://firebasestorage.googleapis.com/v0/b/resumerevival-bb193.appspot.com/o/email.png?alt=media&token=de360c09-caa6-4989-b8c2-e08591b8b808" alt="Email Icon" style="width:16px; margin-right:4px;" />${personalInformation.email}</p>\n`;
    }
    if (personalInformation.phone) {
      html += `<p data-element="phone" style="margin: 5px 0;"><img src="https://firebasestorage.googleapis.com/v0/b/resumerevival-bb193.appspot.com/o/phone.png?alt=media&token=350965e3-0c60-4ac8-b1e9-d926dab6563a" alt="Phone Icon" style="width:16px; margin-right:4px;" />${personalInformation.phone}</p>\n`;
    }
    if (personalInformation.address) {
      html += `<p data-element="address" style="margin: 5px 0;"><img src="https://firebasestorage.googleapis.com/v0/b/resumerevival-bb193.appspot.com/o/location_on_24dp_000000_FILL0_wght0_GRAD0_opsz24.png?alt=media&token=6c07d344-d056-46e9-83dc-4e05dd5e566a" alt="Address Icon" style="width:16px; margin-right:4px;" />${personalInformation.address}</p>\n`;
    }
    // Social links
    if (personalInformation.linkedinUrl) {
      const cleanLinkedIn = personalInformation.linkedinUrl
        .replace(/https?:\/\/(www\.)?/, "")
        .replace(/\/$/, "");
      html += `<p data-element="linkedin" style="margin: 5px 0;"><img src="https://firebasestorage.googleapis.com/v0/b/resumerevival-bb193.appspot.com/o/f92d3760fc8301b2129d40b47c6160d5.png?alt=media&token=5bd94ca8-155e-4120-8f0d-4522fe60dbbe" alt="LinkedIn Icon" style="width:16px; margin-right:4px;" />${cleanLinkedIn}</p>\n`;
    }
    if (personalInformation.githubUrl) {
      const cleanGithub = personalInformation.githubUrl
        .replace(/https?:\/\/(www\.)?/, "")
        .replace(/\/$/, "");
      html += `<p data-element="github" style="margin: 5px 0;"><img src="https://firebasestorage.googleapis.com/v0/b/resumerevival-bb193.appspot.com/o/github.svg?alt=media&token=26e77e68-a792-4617-9581-025159bf9e1b" alt="GitHub Icon" style="width:16px; margin-right:4px;" />${cleanGithub}</p>\n`;
    }
    if (personalInformation.portfolioUrl) {
      const cleanPortfolio = personalInformation.portfolioUrl
        .replace(/https?:\/\/(www\.)?/, "")
        .replace(/\/$/, "");
      html += `<p data-element="portfolio" style="margin: 5px 0;"><strong>Portfolio:</strong> ${cleanPortfolio}</p>\n`;
    }
    html += "</div>\n";

    // Skills in sidebar
    if (skills && Array.isArray(skills) && skills.length > 0) {
      html += `<div data-section="skills">
      <h2 data-section-header="true" data-element="section-header" style="color: #2c3e50; margin-top: 20px;">Skills</h2>\n
      <hr style="border: none; height: 1px; background-color: #ddd; margin: 10px 0 15px 0;">\n`;
      let categorizedSkills = {};
      let isCategorized = false;
      skills.forEach((skill) => {
        if (typeof skill === "object" && skill.category && skill.name) {
          isCategorized = true;
          if (!categorizedSkills[skill.category]) {
            categorizedSkills[skill.category] = [];
          }
          categorizedSkills[skill.category].push(skill.name);
        }
      });
      if (isCategorized) {
        Object.keys(categorizedSkills).forEach((category) => {
          html += `<p data-element="skill-category" style="margin: 10px 0;"><strong>${category}:</strong></p>\n`;
          html += `<ul data-element="skill-list" style="margin: 5px 0 15px 0; padding-left: 20px;">\n`;
          categorizedSkills[category].forEach((skill) => {
            html += `<li data-element="skill-item">${skill}</li>\n`;
          });
          html += `</ul>\n`;
        });
      } else {
        html += `<ul data-element="skill-list" style="margin: 10px 0; padding-left: 20px;">\n`;
        skills.forEach((skill) => {
          if (typeof skill === "string") {
            html += `<li data-element="skill-item">${skill}</li>\n`;
          } else {
            html += `<li data-element="skill-item">${
              skill.name || skill
            }</li>\n`;
          }
        });
        html += `</ul>\n`;
      }
      html += `</div>\n`;
    }

    // Education in sidebar
    if (education && education.length > 0) {
      html += `<div data-section="education">
      <h2 data-section-header="true" data-element="section-header" style="color: #2c3e50; margin-top: 20px;">Education</h2>\n
      <hr style="border: none; height: 1px; background-color: #ddd; margin: 10px 0 15px 0;">\n`;
      education.forEach((edu) => {
        const degree = edu.degree || "";
        const field = edu.fieldOfStudy ? `in ${edu.fieldOfStudy}` : "";
        html += `<div data-element="education-item">
          <h3 data-element="education-degree" style="margin: 10px 0 5px 0; color: #34495e;">${degree} ${field}</h3>\n`;
        const gradDate = edu.graduationDate
          ? formatDate(edu.graduationDate)
          : "";
        html += `<p data-element="education-institution" style="margin: 5px 0;"><em>${
          edu.institution || ""
        }</em></p>\n`;
        html += `<p data-element="education-date" style="margin: 5px 0;">${gradDate}</p>\n`;
        if (edu.gpa) {
          html += `<p data-element="education-gpa" style="margin: 5px 0;"><strong>GPA:</strong> ${edu.gpa}</p>\n`;
        }
        html += `</div>\n`;
      });
      html += `</div>\n`;
    }
  }

  // Close the sidebar
  html += `
  </div>
  
  <!-- Right main content (70% width) -->
  <div data-layout="main-content" style="width: 70%; padding: 20px;">
`;

  // Professional Summary / Objective in main content
  if (personalInformation && personalInformation.professionalSummary) {
    html += `<div data-section="summary">
      <h2 data-section-header="true" data-element="section-header" style="color: #2c3e50;">Professional Summary</h2>\n
      <hr style="border: none; height: 1px; background-color: #ddd; margin: 10px 0 15px 0;">\n
      <p data-element="summary-content" style="margin: 10px 0 20px 0;">${personalInformation.professionalSummary}</p>\n
    </div>\n`;
  } else if (targetJobInformation && targetJobInformation.professionalSummary) {
    html += `<div data-section="summary">
      <h2 data-section-header="true" data-element="section-header" style="color: #2c3e50;">Professional Summary</h2>\n
      <hr style="border: none; height: 1px; background-color: #ddd; margin: 10px 0 15px 0;">\n
      <p data-element="summary-content" style="margin: 10px 0 20px 0;">${targetJobInformation.professionalSummary}</p>\n
    </div>\n`;
  }

  // Work Experience in main content
  if (workExperience && workExperience.length > 0) {
    html += `<div data-section="experience">
      <h2 data-section-header="true" data-element="section-header" style="color: #2c3e50; margin-top: 20px;">Work Experience</h2>\n
      <hr style="border: none; height: 1px; background-color: #ddd; margin: 10px 0 15px 0;">\n`;
    workExperience.forEach((job) => {
      html += `<div data-element="job">
        <h3 data-element="job-title" style="margin: 15px 0 5px 0; color: #34495e;">${
          job.jobTitle || ""
        }</h3>\n
        <p data-element="job-company" style="margin: 5px 0;"><strong>${
          job.companyName || job.company || ""
        }</strong></p>\n`;
      const startDate = formatDate(job.startDate);
      const endDate =
        !job.endDate || job.endDate === "9999-01-01"
          ? "Present"
          : formatDate(job.endDate);
      html += `<p data-element="job-period" style="margin: 5px 0; color: #7f8c8d;"><em>${startDate} - ${endDate} | ${
        job.location || ""
      }</em></p>\n`;
      if (job.description) {
        html += `<p data-element="job-description" style="margin: 10px 0;">${job.description}</p>\n`;
      }
      if (job.responsibilities) {
        html += `<ul data-element="job-responsibilities" style="margin: 10px 0; padding-left: 20px;">\n`;
        if (typeof job.responsibilities === "string") {
          const responsibilitiesList = job.responsibilities
            .split(/[\n\r]+|(?<=\.)(?=\s)/)
            .map((item) => item.trim())
            .filter((item) => item.length > 0);
          responsibilitiesList.forEach((resp) => {
            html += `<li>${resp}</li>\n`;
          });
        } else if (Array.isArray(job.responsibilities)) {
          job.responsibilities.forEach((resp) => {
            if (typeof resp === "string") {
              html += `<li>${resp}</li>\n`;
            } else if (resp && typeof resp === "object") {
              html += `<li>${resp.text || JSON.stringify(resp)}</li>\n`;
            }
          });
        }
        html += `</ul>\n`;
      }
      if (job.achievements && job.achievements.length > 0) {
        html += `<p data-element="achievements-header" style="margin: 10px 0 5px 0;"><strong>Achievements:</strong></p>\n
        <ul data-element="job-achievements" style="margin: 5px 0; padding-left: 20px;">\n`;
        job.achievements.forEach((achievement) => {
          html += `<li>${achievement}</li>\n`;
        });
        html += `</ul>\n`;
      }
      if (job.technologies && job.technologies.length > 0) {
        html += `<p data-element="job-technologies" style="margin: 10px 0;"><strong>Technologies Used:</strong> ${job.technologies.join(
          ", "
        )}</p>\n`;
      }
      html += `</div>\n`;
    });
    html += `</div>\n`;
  }

  // Additional Qualifications in main content
  if (additionalQualifications) {
    html += `<div data-section="additional-qualifications">`;

    // If additionalQualifications is an object with .skills
    if (
      !Array.isArray(additionalQualifications) &&
      additionalQualifications.skills &&
      additionalQualifications.skills.length > 0
    ) {
      html += `<div data-element="additional-skills">
        <h2 data-section-header="true" data-element="section-header" style="color: #2c3e50; margin-top: 20px;">Additional Skills</h2>\n
        <hr style="border: none; height: 1px; background-color: #ddd; margin: 10px 0 15px 0;">\n`;
      if (additionalQualifications.skillsSummary) {
        html += `<p data-element="skills-summary">${escapeHtml(
          additionalQualifications.skillsSummary
        )}</p>\n`;
      }
      if (additionalQualifications.skillsWithDescriptions) {
        html += `<div data-element="skills-with-descriptions">`;
        additionalQualifications.skillsWithDescriptions.forEach((skill) => {
          html += `<p data-element="skill-with-description"><strong>${escapeHtml(
            skill.name
          )}</strong>: ${escapeHtml(skill.description)}</p>\n`;
        });
        html += `</div>`;
      } else {
        const skillList = additionalQualifications.skills
          .map((s) => escapeHtml(s))
          .join(", ");
        html += `<p data-element="skills-list">${skillList}</p>\n`;
      }
      html += `</div>\n`;
    }

    // If additionalQualifications is an array
    if (Array.isArray(additionalQualifications)) {
      const qualificationsByType = {};
      additionalQualifications.forEach((qual) => {
        if (!qualificationsByType[qual.qualificationType]) {
          qualificationsByType[qual.qualificationType] = [];
        }
        qualificationsByType[qual.qualificationType].push(qual.details);
      });

      // Work Authorization
      if (qualificationsByType["Work Authorization"]) {
        html += `<div data-section="work-authorization">
          <h2 data-section-header="true" data-element="section-header" style="color: #2c3e50; margin-top: 20px;">Work Authorization</h2>\n
          <hr style="border: none; height: 1px; background-color: #ddd; margin: 10px 0 15px 0;">\n
          <ul data-element="authorization-list" style="margin: 10px 0; padding-left: 20px;">\n`;
        qualificationsByType["Work Authorization"].forEach((auth) => {
          html += `<li data-element="authorization-item"><strong>Status</strong>: ${
            auth.status || "Not specified"
          }</li>\n`;
        });
        html += `</ul>\n</div>\n`;
      }

      // Security Clearance
      if (qualificationsByType["Security Clearance"]) {
        html += `<div data-section="security-clearance">
          <h2 data-section-header="true" data-element="section-header" style="color: #2c3e50; margin-top: 20px;">Security Clearance</h2>\n
          <hr style="border: none; height: 1px; background-color: #ddd; margin: 10px 0 15px 0;">\n
          <ul data-element="clearance-list" style="margin: 10px 0; padding-left: 20px;">\n`;
        qualificationsByType["Security Clearance"].forEach((clearance) => {
          html += `<li data-element="clearance-item"><strong>Level</strong>: ${
            clearance.level || "Not specified"
          }</li>\n`;
        });
        html += `</ul>\n</div>\n`;
      }

      // Personal Projects
      if (qualificationsByType["Personal Project"]) {
        html += `<div data-section="projects">
          <h2 data-section-header="true" data-element="section-header" style="color: #2c3e50; margin-top: 20px;">Personal Projects</h2>\n
          <hr style="border: none; height: 1px; background-color: #ddd; margin: 10px 0 15px 0;">\n`;
        qualificationsByType["Personal Project"].forEach((project) => {
          html += `<div data-element="project">
            <h3 data-element="project-name" style="margin: 15px 0 5px 0; color: #34495e;">${escapeHtml(
              project.projectName
            )}</h3>\n`;
          if (project.description) {
            html += `<p data-element="project-description" style="margin: 10px 0;">${escapeHtml(
              project.description
            )}</p>\n`;
          }
          if (project.skills) {
            const skillsText = Array.isArray(project.skills)
              ? project.skills.join(", ")
              : project.skills;
            html += `<p data-element="project-skills" style="margin: 5px 0;"><strong class="preserve-bold">Skills Utilized:</strong> ${escapeHtml(
              skillsText
            )}</p>\n`;
          }
          if (project.outcome) {
            html += `<p data-element="project-outcome" style="margin: 5px 0;"><strong class="preserve-bold">Outcome:</strong> ${escapeHtml(
              project.outcome
            )}</p>\n`;
          }
          if (project.projectUrl) {
            html += `<p data-element="project-url" style="margin: 5px 0;"><strong>Project URL:</strong> <a href="${escapeHtml(
              project.projectUrl
            )}" style="color: #3498db;">${escapeHtml(
              project.projectUrl
            )}</a></p>\n`;
          }
          html += `</div>\n`;
        });
        html += `</div>\n`;
      }

      // Certification
      if (qualificationsByType["Certification"]) {
        html += `<div data-section="certifications">
          <h2 data-section-header="true" data-element="section-header" style="color: #2c3e50; margin-top: 20px;">Certifications</h2>\n
          <hr style="border: none; height: 1px; background-color: #ddd; margin: 10px 0 15px 0;">\n
          <ul data-element="certification-list" style="margin: 10px 0; padding-left: 20px;">\n`;
        qualificationsByType["Certification"].forEach((cert) => {
          html += `<li data-element="certification-item"><strong>${escapeHtml(
            cert.name || "Certification"
          )}</strong>`;
          if (cert.issuer) {
            html += ` (Issuer: <strong>${escapeHtml(cert.issuer)}</strong>`;
            if (cert.dateObtained) {
              html += `, <strong>${escapeHtml(
                formatDate(cert.dateObtained)
              )}</strong>`;
            }
            html += `)`;
          }
          if (cert.description) {
            html += `<br>${escapeHtml(cert.description)}`;
          }
          html += `</li>\n`;
        });
        html += `</ul>\n</div>\n`;
      }

      // Patent
      if (qualificationsByType["Patent"]) {
        html += `<div data-section="patents">
          <h2 data-section-header="true" data-element="section-header" style="color: #2c3e50; margin-top: 20px;">Patents</h2>\n
          <hr style="border: none; height: 1px; background-color: #ddd; margin: 10px 0 15px 0;">\n
          <ul data-element="patent-list" style="margin: 10px 0; padding-left: 20px;">\n`;
        qualificationsByType["Patent"].forEach((patent) => {
          html += `<li data-element="patent-item"><strong>${escapeHtml(
            patent.patentTitle
          )}</strong> (Patent #${escapeHtml(patent.patentNumber)})`;
          if (patent.dateGranted) {
            html += ` - Granted: ${escapeHtml(formatDate(patent.dateGranted))}`;
          }
          if (patent.description) {
            html += `<br>${escapeHtml(patent.description)}`;
          }
          html += `</li>\n`;
        });
        html += `</ul>\n</div>\n`;
      }

      // Publication
      if (qualificationsByType["Publication"]) {
        html += `<div data-section="publications">
          <h2 data-section-header="true" data-element="section-header" style="color: #2c3e50; margin-top: 20px;">Publications</h2>\n
          <hr style="border: none; height: 1px; background-color: #ddd; margin: 10px 0 15px 0;">\n
          <ul data-element="publication-list" style="margin: 10px 0; padding-left: 20px;">\n`;
        qualificationsByType["Publication"].forEach((pub) => {
          html += `<li data-element="publication-item"><strong>${escapeHtml(
            pub.title
          )}</strong>`;
          if (pub.publisher) {
            html += `, <strong>Publisher:</strong> ${escapeHtml(
              pub.publisher
            )}`;
          }
          if (pub.date) {
            html += ` (<strong>${escapeHtml(formatDate(pub.date))}</strong>)`;
          }
          if (pub.authors) {
            html += `<br><strong>Authors</strong>: ${escapeHtml(pub.authors)}`;
          }
          if (pub.description) {
            html += `<br>${escapeHtml(pub.description)}`;
          }
          if (pub.url) {
            html += `<br><a href="${escapeHtml(pub.url)}">Read Publication</a>`;
          }
          html += `</li>\n`;
        });
        html += `</ul>\n</div>\n`;
      }

      // Research Experience
      if (qualificationsByType["Research Experience"]) {
        html += `<div data-section="research">
          <h2 data-section-header="true" data-element="section-header" style="color: #2c3e50; margin-top: 20px;">Research Experience</h2>\n
          <hr style="border: none; height: 1px; background-color: #ddd; margin: 10px 0 15px 0;">\n`;
        qualificationsByType["Research Experience"].forEach((research) => {
          html += `<div data-element="research-item">
            <h3 data-element="research-title" style="color: #34495e; margin: 15px 0 5px 0;">${escapeHtml(
              research.projectTitle
            )}</h3>\n`;
          if (research.role) {
            html += `<p data-element="research-role"><strong>Role</strong>: ${escapeHtml(
              research.role
            )}</p>\n`;
          }
          if (research.institution) {
            html += `<p data-element="research-institution"><strong>Institution</strong>: ${escapeHtml(
              research.institution
            )}</p>\n`;
          }
          if (research.startDate && research.endDate) {
            html += `<p data-element="research-duration"><strong>Duration</strong>: ${escapeHtml(
              formatDate(research.startDate)
            )} - ${escapeHtml(formatDate(research.endDate))}</p>\n`;
          } else if (research.startDate) {
            html += `<p data-element="research-started"><strong>Started</strong>: ${escapeHtml(
              formatDate(research.startDate)
            )}</p>\n`;
          }
          if (research.description) {
            html += `<p data-element="research-description">${escapeHtml(
              research.description
            )}</p>\n`;
          }
          if (research.outcomes) {
            html += `<p data-element="research-outcomes"><strong>Outcomes</strong>: ${escapeHtml(
              research.outcomes
            )}</p>\n`;
          }
          html += `</div>\n`;
        });
        html += `</div>\n`;
      }

      // Volunteering
      if (qualificationsByType["Volunteering"]) {
        html += `<div data-section="volunteering">
          <h2 data-section-header="true" data-element="section-header" style="color: #2c3e50; margin-top: 20px;">Volunteer Experience</h2>\n
          <hr style="border: none; height: 1px; background-color: #ddd; margin: 10px 0 15px 0;">\n`;
        qualificationsByType["Volunteering"].forEach((volunteer) => {
          html += `<div data-element="volunteer-item">
            <h3 data-element="volunteer-organization" style="color: #34495e; margin: 15px 0 5px 0;">${escapeHtml(
              volunteer.organization
            )}</h3>\n`;
          if (volunteer.role) {
            html += `<p data-element="volunteer-role"><strong>Role</strong>: ${escapeHtml(
              volunteer.role
            )}</p>\n`;
          }
          if (volunteer.startDate && volunteer.endDate) {
            html += `<p data-element="volunteer-duration"><strong>Duration</strong>: ${escapeHtml(
              formatDate(volunteer.startDate)
            )} - ${escapeHtml(formatDate(volunteer.endDate))}</p>\n`;
          } else if (volunteer.startDate) {
            html += `<p data-element="volunteer-started"><strong>Started</strong>: ${escapeHtml(
              formatDate(volunteer.startDate)
            )}</p>\n`;
          }
          if (volunteer.description) {
            html += `<p data-element="volunteer-description">${escapeHtml(
              volunteer.description
            )}</p>\n`;
          }
          html += `</div>\n`;
        });
        html += `</div>\n`;
      }

      // Professional Development
      if (qualificationsByType["Professional Development"]) {
        html += `<div data-section="professional-development">
          <h2 data-section-header="true" data-element="section-header" style="color: #2c3e50; margin-top: 20px;">Professional Development</h2>\n
          <hr style="border: none; height: 1px; background-color: #ddd; margin: 10px 0 15px 0;">\n
          <ul data-element="development-list" style="margin: 10px 0; padding-left: 20px;">\n`;
        qualificationsByType["Professional Development"].forEach((dev) => {
          html += `<li data-element="development-item"><strong>${escapeHtml(
            dev.name
          )}</strong>`;
          if (dev.type) {
            html += ` (${escapeHtml(dev.type)})`;
            html += `<br>${escapeHtml(dev.description)}`;
          }
          html += `</li>\n`;
        });
        html += `</ul>\n</div>\n`;
      }

      // Awards
      if (qualificationsByType["Award"]) {
        html += `<div data-section="awards">
          <h2 data-section-header="true" data-element="section-header" style="color: #2c3e50; margin-top: 20px;">Awards & Honors</h2>\n
          <hr style="border: none; height: 1px; background-color: #ddd; margin: 10px 0 15px 0;">\n
          <ul data-element="awards-list" style="margin: 10px 0; padding-left: 20px;">\n`;
        qualificationsByType["Award"].forEach((award) => {
          html += `<li data-element="award-item"><strong>${escapeHtml(
            award.title
          )}</strong>`;
          if (award.issuer) {
            html += ` from <strong>${escapeHtml(award.issuer)}</strong>`;
          }
          if (award.date) {
            html += ` (<strong>${escapeHtml(formatDate(award.date))}</strong>)`;
          }
          if (award.description) {
            html += `<br>${escapeHtml(award.description)}`;
          }
          html += `</li>\n`;
        });
        html += `</ul>\n</div>\n`;
      }

      // Languages
      if (qualificationsByType["Language"]) {
        html += `<div data-section="languages">
          <h2 data-section-header="true" data-element="section-header" style="color: #2c3e50; padding-bottom: 5px; margin-top: 20px;">Languages</h2>\n
          <hr style="border: none; height: 1px; background-color: #ddd; margin: 10px 0 15px 0;">\n`;
        qualificationsByType["Language"].forEach((lang) => {
          html += `<p data-element="language-item"><strong>${escapeHtml(
            lang.name
          )}</strong>: ${escapeHtml(lang.proficiency || "Fluent")}</p>\n`;
        });
        html += `</div>\n`;
      } else if (
        additionalQualifications.languages &&
        additionalQualifications.languages.length > 0
      ) {
        html += `<div data-section="languages">
          <h2 data-section-header="true" data-element="section-header" style="color: #2c3e50; margin-top: 20px;">Languages</h2>\n
          <hr style="border: none; height: 1px; background-color: #ddd; margin: 10px 0 15px 0;">\n`;
        if (additionalQualifications.languagesWithProficiency) {
          additionalQualifications.languagesWithProficiency.forEach((lang) => {
            html += `<p data-element="language-item"><strong>${escapeHtml(
              lang.name
            )}</strong>: ${escapeHtml(lang.proficiency)}</p>\n`;
          });
        } else {
          const languageList = additionalQualifications.languages
            .map((l) => escapeHtml(l))
            .join(", ");
          html += `<p data-element="language-list">${languageList}</p>\n`;
        }
        html += `</div>\n`;
      }
    }

    html += `</div>\n`; // Close additional qualifications section
  }

  // References in main content
  if (references && references.length > 0) {
    html += `<div data-section="references">
      <h2 data-section-header="true" data-element="section-header" style="color: #2c3e50; margin-top: 20px;">References</h2>\n
          <hr style="border: none; height: 1px; background-color: #ddd; margin: 10px 0 15px 0;">\n`;
    if (references[0].available === false) {
      html += `<p data-element="references-available"><em>References available upon request</em></p>\n`;
    } else {
      html += `<ul data-element="references-list" style="list-style: none; padding-left: 0;">\n`;
      references.forEach((ref) => {
        html += `<li data-element="reference-item" style="margin-bottom: 10px;">\n`;
        html += `<p style="margin: 0 0 5px 0; color: #34495e;"><strong>${escapeHtml(
          ref.name
        )}</strong>`;
        if (ref.title) html += `, ${escapeHtml(ref.title)}`;
        if (ref.company) html += `, ${escapeHtml(ref.company)}`;
        html += `</p>\n`;
        if (ref.email || ref.phone) {
          html += `<p style="margin: 0; color: #7f8c8d;">`;
          if (ref.email) html += `${escapeHtml(ref.email)}`;
          if (ref.email && ref.phone) html += ` | `;
          if (ref.phone) html += `${escapeHtml(ref.phone)}`;
          html += `</p>\n`;
        }
        html += `</li>\n`;
      });
      html += `</ul>\n`;
    }
    html += `</div>\n`;
  }

  // Close the main content and container
  html += `
  </div>
</div>
`;

  return html;
};

/**
 * Helper function to format dates consistently
 * @param {string} dateString - Date string to format
 * @returns {string} Formatted date string
 */
function formatDate(dateString) {
  if (!dateString) return "";
  try {
    const date = new Date(dateString);
    if (isNaN(date.getTime())) {
      return dateString;
    }
    const options = { month: "long", year: "numeric" };
    return date.toLocaleDateString(undefined, options);
  } catch (error) {
    return dateString;
  }
}

export default modernTemplateHtml;
